package org.teddapp.project.ui.nav

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.APP_VERSION
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import org.teddapp.project.server.LocalServerSession
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.settings.LocalSyncBindings
import org.teddapp.project.ui.settings.SettingsScreen
import org.teddapp.project.ui.teddClickable
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.imePadding
import androidx.compose.ui.platform.LocalDensity

/**
 * Barre du bas (téléphone) : les six piliers, puis « Plus ». Projets et Compétences
 * s'atteignent depuis Objectifs ; tout le reste (Portfolio, Trousseau, Sauvegarde,
 * Réglages, et le back-office pour le supra_user) est derrière « Plus ».
 *
 * La feuille « Plus » et les réglages s'ouvrent d'ici, au-dessus de l'écran : les écrans
 * qui posent la barre n'ont rien à gérer.
 */
@Composable
fun TeddBottomNav(
    active: Section,
    onNavigate: (Section) -> Unit,
    enabled: Set<Section> = Section.entries.toSet(),
    modifier: Modifier = Modifier,
) {
    var moreOpen by remember { mutableStateOf(false) }
    var settingsOpen by remember { mutableStateOf(false) }
    val supra = LocalServerSession.current?.isSupra == true
    val behindMore = listOfNotNull(Section.Portfolio, Section.MindMaps, Section.Vault, Section.Card, Section.Backup, if (supra) Section.Admin else null)

    // Pendant qu'on écrit, la barre laisse sa hauteur au contenu.
    val keyboardOpen = WindowInsets.ime.getBottom(LocalDensity.current) > 0
    if (!keyboardOpen) Row(
        modifier = modifier
            .fillMaxWidth()
            .height(72.dp)
            .background(TeddTokens.Surface)
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
            .padding(horizontal = 4.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        BOTTOM_NAV_SECTIONS.forEach { section ->
            BottomNavItem(
                modifier = Modifier.weight(1f),
                icon = section.icon,
                label = section.label,
                isActive = section == active.navHome,
                isEnabled = section in enabled,
                onClick = { if (section != active) onNavigate(section) },
            )
        }
        BottomNavItem(
            modifier = Modifier.weight(0.8f),
            icon = "more",
            label = "Plus",
            isActive = active in behindMore || moreOpen,
            isEnabled = true,
            onClick = { moreOpen = true },
        )
    }

    if (moreOpen) {
        Dialog(onDismissRequest = { moreOpen = false }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(androidx.compose.ui.graphics.Color.Black.copy(alpha = 0.32f))
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { moreOpen = false },
                contentAlignment = Alignment.BottomCenter,
            ) {
                TeddMoreSheet(
                    active = active,
                    onNavigate = { moreOpen = false; if (it != active) onNavigate(it) },
                    onOpenSettings = { moreOpen = false; settingsOpen = true },
                )
            }
        }
    }

    if (settingsOpen) {
        Dialog(onDismissRequest = { settingsOpen = false }, properties = DialogProperties(usePlatformDefaultWidth = false)) {
            val bindings = LocalSyncBindings.current
            val syncState by (bindings?.state?.collectAsState() ?: remember { mutableStateOf(null) })
            SettingsScreen(
                onClose = { settingsOpen = false },
                modifier = Modifier.fillMaxSize().statusBarsPadding().navigationBarsPadding().imePadding(),
                onOpenBackup = { settingsOpen = false; onNavigate(Section.Backup) },
                onOpenPortfolio = { settingsOpen = false; onNavigate(Section.Portfolio) },
                syncState = syncState,
                onToggleSync = bindings?.let { b -> { on -> b.onToggle(on) } },
                onSyncNow = bindings?.let { b -> { b.onSyncNow() } },
            )
        }
    }
}

/** La feuille « Plus » : ce qui n'a pas de place dans la barre du bas. */
@Composable
fun TeddMoreSheet(active: Section, onNavigate: (Section) -> Unit, onOpenSettings: () -> Unit, modifier: Modifier = Modifier) {
    val supra = LocalServerSession.current?.isSupra == true
    val sections = listOfNotNull(Section.Portfolio, Section.MindMaps, Section.Vault, Section.Card, Section.Backup, if (supra) Section.Admin else null)
    val shape = RoundedCornerShape(topStart = TeddTokens.RLg, topEnd = TeddTokens.RLg)
    Column(
        modifier = modifier
            .fillMaxWidth()
            .background(TeddTokens.SurfaceOverlay, shape)
            .border(1.dp, TeddTokens.Line, shape)
            // Un toucher dans la feuille ne la ferme pas.
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {}
            .navigationBarsPadding()
            .padding(TeddSpace.S3),
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Text("PLUS", color = TeddTokens.InkMuted, style = TeddType.Overline, modifier = Modifier.padding(start = TeddSpace.S3, top = TeddSpace.S2, bottom = TeddSpace.S2))
        sections.forEach { section ->
            MoreRow(
                icon = section.icon,
                label = if (section == Section.Backup) "Sauvegarde et synchro" else section.label,
                color = section.moduleColor,
                current = section == active,
            ) { onNavigate(section) }
        }
        MoreRow(icon = "settings", label = "Réglages", color = TeddTokens.InkMuted, current = false, onClick = onOpenSettings)
    }
}

/** Une destination de la feuille « Plus ». */
@Composable
private fun MoreRow(icon: String, label: String, color: androidx.compose.ui.graphics.Color, current: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .teddClickable(onClick = onClick, fill = if (current) TeddTokens.SurfaceSunken else androidx.compose.ui.graphics.Color.Transparent)
            .height(48.dp)
            .padding(horizontal = TeddSpace.S3),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        TeddIcon(name = icon, size = 20.dp, color = color)
        Text(label, color = TeddTokens.Ink, style = TeddType.Label.copy(fontSize = 15.sp), modifier = Modifier.weight(1f))
        TeddIcon(name = "chevron-right", size = 16.dp, color = TeddTokens.InkSubtle)
    }
}

@Composable
private fun BottomNavItem(
    modifier: Modifier,
    icon: String,
    label: String,
    isActive: Boolean,
    isEnabled: Boolean,
    onClick: () -> Unit,
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .clickable(
                enabled = isEnabled,
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            )
            .padding(vertical = 6.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Box(
            modifier = Modifier
                .size(36.dp, 26.dp)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .then(
                    if (isActive) Modifier.background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RMd))
                    else Modifier
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(
                name = icon,
                size = 18.dp,
                color = when {
                    isActive -> TeddTokens.LimeStrong
                    isEnabled -> TeddTokens.InkMuted
                    else -> TeddTokens.InkSubtle
                },
                strokeWidth = if (isActive) 2.dp else 1.7.dp,
            )
        }
        Text(
            text = label,
            maxLines = 1,
            softWrap = false,
            color = when {
                isActive -> TeddTokens.LimeStrong
                isEnabled -> TeddTokens.InkMuted
                else -> TeddTokens.InkSubtle
            },
            fontSize = 10.sp,
            fontWeight = if (isActive) FontWeight.SemiBold else FontWeight.Medium,
        )
    }
}
